/**
 * useChat Hook
 * Custom hook for managing chat functionality with the Nexgo Concierge API
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  ChatMessage,
  UseChatResult,
  ChatRequest,
  ChatResponse,
} from '@/types/chatTypes';
import {
  sendChatResponse,
  fetchChatFollowUps,
  fetchChatExperiences,
  generateSessionId,
  generateUserId,
  validateChatRequest,
} from '@/services/chatService';

// Note: We removed response-level deduplication because:
// 1. API-level request deduplication already handles duplicate API calls
// 2. When multiple callers await the same promise, they should all process the response
// 3. Response deduplication was too aggressive and blocked valid responses
// The API deduplication ensures only one HTTP request is made, and all awaiters get the same response
// which is the correct behavior - each caller should process the response for their own component instance

export interface UseChatOptions {
  initialMessages?: ChatMessage[];
  initialSessionId?: string | null;
}

export function useChat(options?: UseChatOptions): ExtendedUseChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>(
    options?.initialMessages || []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingResponse, setIsLoadingResponse] = useState(false);
  const [isLoadingFollowUps, setIsLoadingFollowUps] = useState(false);
  const [isLoadingExperiences, setIsLoadingExperiences] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Initialize session ID synchronously to prevent race conditions
  const [sessionId, setSessionId] = useState<string | null>(() => {
    return options?.initialSessionId || generateSessionId();
  });

  // Initialize user ID synchronously to prevent race conditions
  const userIdRef = useRef<string | null>(null);

  // Initialize user ID immediately if not already set
  if (!userIdRef.current) {
    if (typeof window !== 'undefined') {
      try {
        userIdRef.current = generateUserId();
      } catch (err) {
        console.error('Error initializing user ID:', err);
        // Fallback: generate without localStorage
        userIdRef.current = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
    } else {
      // For SSR, generate a temporary ID
      userIdRef.current = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  }

  const messageIdCounter = useRef(options?.initialMessages?.length || 0);

  // Extract initialMessages for dependency tracking
  const initialMessages = options?.initialMessages;

  // Update messages when initialMessages change (for async loading)
  useEffect(() => {
    if (initialMessages && initialMessages.length > 0) {
      console.log('🔄 [useChat] Initial messages available:', {
        count: initialMessages.length,
        firstMessage: initialMessages[0]?.content?.substring(0, 50),
        lastMessage: initialMessages[
          initialMessages.length - 1
        ]?.content?.substring(0, 50),
      });

      // Use functional update to check current state
      setMessages(prev => {
        // Only update if messages are empty or different
        if (prev.length === 0) {
          console.log(
            '✅ [useChat] Setting initial messages (current messages empty)'
          );
          messageIdCounter.current = initialMessages.length;
          return initialMessages;
        }

        // Check if content is different
        if (prev.length !== initialMessages.length) {
          console.log('✅ [useChat] Message count differs, updating');
          messageIdCounter.current = initialMessages.length;
          return initialMessages;
        }

        const isDifferent = prev.some((msg, idx) => {
          const initialMsg = initialMessages[idx];
          return (
            !initialMsg ||
            msg.id !== initialMsg.id ||
            msg.content !== initialMsg.content
          );
        });

        if (isDifferent) {
          console.log('✅ [useChat] Message content differs, updating');
          messageIdCounter.current = initialMessages.length;
          return initialMessages;
        }

        console.log('⚠️ [useChat] Messages are the same, keeping current');
        return prev;
      });
    }
  }, [initialMessages]);

  // Update sessionId when initialSessionId changes
  // Important: Once a sessionId is set from initialSessionId, it should persist
  useEffect(() => {
    if (options?.initialSessionId) {
      // Always update if initialSessionId is provided and different
      // This ensures loaded conversations maintain their session_id
      if (options.initialSessionId !== sessionId) {
        console.log(
          '🔄 [useChat] Setting sessionId from initialSessionId:',
          options.initialSessionId
        );
        setSessionId(options.initialSessionId);
      }
    }
  }, [options?.initialSessionId, sessionId]);

  /**
   * Generate a unique message ID
   */
  const generateMessageId = useCallback(() => {
    messageIdCounter.current += 1;
    return `msg_${Date.now()}_${messageIdCounter.current}`;
  }, []);

  /**
   * Add a message to the chat
   */
  const addMessage = useCallback(
    (message: Omit<ChatMessage, 'id'>) => {
      const newMessage: ChatMessage = {
        ...message,
        id: generateMessageId(),
      };

      setMessages(prev => [...prev, newMessage]);
      return newMessage;
    },
    [generateMessageId]
  );

  /**
   * Send a message to the chat API
   */
  const sendMessage = useCallback(
    async (message: string, audioMessage?: string) => {
      if (!userIdRef.current || !sessionId) {
        setError('Chat not initialized. Please refresh the page.');
        return;
      }

      // Allow audio-only messages, but require either message or audio
      if (!message.trim() && !audioMessage) {
        setError('Please enter a message or record audio.');
        return;
      }

      setError(null);
      setIsLoading(true);

      try {
        const trimmedMessage = message.trim();

        // Add user message to chat (include audio for playback)
        addMessage({
          role: 'user',
          content: trimmedMessage,
          timestamp: new Date(),
          session_id: sessionId,
          audioBase64: audioMessage, // Store audio for playback in UI
        });

        // Prepare API request
        // Build request object - message field should always be present (empty string if no text)
        const request: ChatRequest = {
          user_id: userIdRef.current,
          session_id: sessionId,
          message: trimmedMessage || '',
        };

        // Explicitly add audio_message if it exists
        if (audioMessage && audioMessage.trim().length > 0) {
          request.audio_message = audioMessage;
          console.log('✅ [CHAT] audio_message added to request');
          console.log('   - Length:', audioMessage.length);
          console.log('   - First 30 chars:', audioMessage.substring(0, 30));
          console.log(
            '   - Last 30 chars:',
            audioMessage.substring(audioMessage.length - 30)
          );
        } else {
          console.log('⚠️ [CHAT] No audio_message to add to request');
          console.log('   - audioMessage value:', audioMessage);
          console.log('   - audioMessage type:', typeof audioMessage);
          console.log('   - audioMessage length:', audioMessage?.length);
        }

        // CRITICAL: Verify audio_message is actually in the request object
        console.log(
          '🔍 [CHAT] VERIFICATION - Request object keys:',
          Object.keys(request)
        );
        console.log(
          '🔍 [CHAT] VERIFICATION - Has audio_message key:',
          'audio_message' in request
        );
        console.log(
          '🔍 [CHAT] VERIFICATION - audio_message value exists:',
          !!request.audio_message
        );
        console.log(
          '🔍 [CHAT] VERIFICATION - audio_message length:',
          request.audio_message?.length || 'UNDEFINED'
        );

        // Log audio message details if present (message field log removed)
        if (request.audio_message) {
          console.log('📤 [CHAT] Audio message details:', {
            hasAudioMessage: true,
            audioMessageLength: request.audio_message?.length || 0,
            audioMessageSizeKB: request.audio_message
              ? ((request.audio_message.length * 0.75) / 1024).toFixed(2)
              : '0',
          });
        }

        // Validate request
        const validationErrors = validateChatRequest(request);
        if (validationErrors.length > 0) {
          console.error('❌ [CHAT] Validation errors:', validationErrors);
          throw new Error(`Invalid request: ${validationErrors.join(', ')}`);
        }

        console.log('📤 [CHAT] Starting chat request...');

        // Step 1: Call chat response API
        console.log('🔵 [CHAT] Step 1: Calling chat response API...');
        setIsLoadingResponse(true);

        let responseData: ChatResponse | null = null;
        let responseSessionId = sessionId;
        let responseHash: string | undefined;

        try {
          // Call the regular response endpoint
          responseData = await sendChatResponse(request);

          // Update session ID if changed
          if (
            responseData.session_id &&
            responseData.session_id !== sessionId
          ) {
            console.log(
              '🔄 [useChat] Session ID changed in response:',
              sessionId,
              '->',
              responseData.session_id
            );
            responseSessionId = responseData.session_id;
            setSessionId(responseData.session_id);
          }

          // Extract response hash if available
          if (
            'response_hash' in responseData &&
            typeof responseData.response_hash === 'string'
          ) {
            responseHash = responseData.response_hash;
          }

          // Apply transcription to last user message and add assistant message in one update (avoids batching overwriting transcription)
          const assistantMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'assistant',
            content: responseData.response,
            timestamp: new Date(),
            session_id: responseSessionId,
            locations: responseData.locations,
            followUpQuestions: responseData.followUpQuestions,
            mood: responseData.mood,
            intent: responseData.intent,
            overview: responseData.overview,
            experiences: responseData.experiences,
          };
          setMessages(prev => {
            const next = [...prev];
            if (
              responseData.transcription &&
              next.length > 0 &&
              next[next.length - 1].role === 'user'
            ) {
              next[next.length - 1] = {
                ...next[next.length - 1],
                transcription: responseData.transcription,
              };
            }
            next.push(assistantMessage);
            return next;
          });

          console.log('✅ [useChat] Response received:', {
            contentLength: responseData.response?.length || 0,
            hasSessionId: !!responseSessionId,
            intent: responseData.intent,
            mood: responseData.mood,
            responseHash: responseHash,
            hasLocations: !!responseData.locations?.length,
            hasFollowUps: !!responseData.followUpQuestions?.length,
            hasExperiences: !!responseData.experiences?.length,
          });
        } catch (responseError) {
          console.error('❌ [useChat] Response error:', responseError);
          const errorMessage =
            responseError instanceof Error
              ? responseError.message
              : 'Failed to get chat response';
          setError(errorMessage);

          // Add error message to chat
          addMessage({
            role: 'assistant',
            content: `Sorry, I encountered an error: ${errorMessage}`,
            timestamp: new Date(),
            session_id: sessionId,
          });
        } finally {
          setIsLoadingResponse(false);
        }

        // Step 2: Call follow-ups API (if we have response_hash and follow-ups weren't in response)
        console.log('🟢 [CHAT] Step 2: Calling follow-ups API...');
        setIsLoadingFollowUps(true);
        try {
          if (responseHash && responseSessionId && responseData) {
            // Only fetch follow-ups if they weren't already in the response
            if (
              !responseData.followUpQuestions ||
              responseData.followUpQuestions.length === 0
            ) {
              const followUpsResponse = await fetchChatFollowUps({
                user_id: request.user_id,
                session_id: responseSessionId,
                response_hash: responseHash,
              });

              // Update the last assistant message with follow-ups
              if (followUpsResponse?.followUpQuestions) {
                setMessages(prev => {
                  const lastAssistantIndex = prev
                    .map((msg, idx) => ({ msg, idx }))
                    .reverse()
                    .find(({ msg }) => msg.role === 'assistant')?.idx;

                  if (lastAssistantIndex !== undefined) {
                    return prev.map((msg, idx) => {
                      if (idx === lastAssistantIndex) {
                        return {
                          ...msg,
                          followUpQuestions:
                            followUpsResponse.followUpQuestions,
                        };
                      }
                      return msg;
                    });
                  }
                  return prev;
                });
                console.log('✅ [useChat] Follow-ups added:', {
                  count: followUpsResponse.followUpQuestions.length,
                });
              }
            } else {
              console.log('✅ [useChat] Follow-ups already in response');
            }
          } else {
            console.log('⚠️ [useChat] No response_hash, skipping follow-ups');
          }
        } finally {
          setIsLoadingFollowUps(false);
        }

        // Step 3: Call experiences API only when AI returned results (has locations or experiences)
        const hasResults =
          (responseData?.locations?.length ?? 0) > 0 ||
          (responseData?.experiences?.length ?? 0) > 0;
        setIsLoadingExperiences(true);
        try {
          if (responseHash && responseSessionId && responseData && hasResults) {
            // Only fetch experiences if they weren't already in the response
            if (
              !responseData.experiences ||
              responseData.experiences.length === 0
            ) {
              const experiencesResponse = await fetchChatExperiences({
                user_id: request.user_id,
                session_id: responseSessionId,
                response_hash: responseHash,
              });

              // Update the last assistant message with experiences
              if (experiencesResponse?.experiences) {
                setMessages(prev => {
                  const lastAssistantIndex = prev
                    .map((msg, idx) => ({ msg, idx }))
                    .reverse()
                    .find(({ msg }) => msg.role === 'assistant')?.idx;

                  if (lastAssistantIndex !== undefined) {
                    return prev.map((msg, idx) => {
                      if (idx === lastAssistantIndex) {
                        return {
                          ...msg,
                          experiences: experiencesResponse.experiences,
                        };
                      }
                      return msg;
                    });
                  }
                  return prev;
                });
                console.log('✅ [useChat] Experiences added:', {
                  count: experiencesResponse.experiences.length,
                });
              }
            }
          }
          // When AI returns no results (no locations, no experiences), skip experiences fetch and map
        } finally {
          setIsLoadingExperiences(false);
        }

        console.log('✅ [CHAT] All APIs completed');

        // Clear error on successful response (ensures error is hidden after retry)
        setError(null);
      } catch (err) {
        console.error('Error sending chat message:', err);

        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to send message. Please try again.';
        setError(errorMessage);

        // Add error message to chat for user feedback
        addMessage({
          role: 'assistant',
          content: `Sorry, I encountered an error: ${errorMessage}`,
          timestamp: new Date(),
          session_id: sessionId,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, addMessage, generateMessageId]
  );

  /**
   * Clear all messages and start a new session
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    setSessionId(generateSessionId());
    messageIdCounter.current = 0;
  }, []);

  /**
   * Retry the last message (useful for error recovery)
   */
  const retryLastMessage = useCallback(() => {
    const lastUserMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.role === 'user');

    if (lastUserMessage) {
      // Clear error state immediately
      setError(null);

      // Remove the last assistant message if it was an error
      setMessages(prev => {
        const lastIndex = prev.length - 1;
        if (lastIndex >= 0 && prev[lastIndex].role === 'assistant') {
          return prev.slice(0, -1);
        }
        return prev;
      });

      // Retry with the same content and audio if available
      sendMessage(lastUserMessage.content, lastUserMessage.audioBase64);
    }
  }, [messages, sendMessage]);

  // Combined loading state (true if any API is loading)
  const isLoadingCombined =
    isLoading ||
    isLoadingResponse ||
    isLoadingFollowUps ||
    isLoadingExperiences;

  return {
    messages,
    isLoading: isLoadingCombined, // Keep backward compatibility
    isLoadingResponse,
    isLoadingFollowUps,
    isLoadingExperiences,
    error,
    sendMessage,
    clearMessages,
    sessionId,
    retryLastMessage,
  };
}

/**
 * Extended hook result with retry functionality and separate loading states
 */
export interface ExtendedUseChatResult extends UseChatResult {
  retryLastMessage: () => void;
  isLoadingResponse: boolean;
  isLoadingFollowUps: boolean;
  isLoadingExperiences: boolean;
}
